import React, { createContext, useCallback, useContext, useState, useRef, } from 'react';
import useMutableValue, { mutableValue, } from '@polestar/component-warehouse-react/hooks/useMutableValue';
import { useIsSSR } from '@polestar/component-warehouse-react/contexts/ssr';
export var HeaderPositions;
(function (HeaderPositions) {
    HeaderPositions["static"] = "static";
    HeaderPositions["fixed"] = "fixed";
})(HeaderPositions || (HeaderPositions = {}));
const noProviderFunction = (handleName) => () => {
    // eslint-disable-next-line no-console
    console.warn(`Trying to use header context consumer, but no context provider for "${handleName}". Please make sure you use the useHeader inside a <PolestarApp /> or check if there are multiple @polestar/component-warehouse-react versions installed due to (for example) shared modules hard dependencies.`);
};
const HeaderContext = createContext({
    headerVisible: mutableValue(true),
    headerPosition: mutableValue(HeaderPositions.static),
    headerHeight: mutableValue(0),
    baseHeaderHeight: mutableValue(0),
    safeAreaHeight: 0,
    subNavigationSafeAreaHeight: 0,
    offerBarHeight: 0,
    showHeader: noProviderFunction('showHeader'),
    hideHeader: noProviderFunction('hideHeader'),
    setSafeAreaHeight: noProviderFunction('setSafeAreaHeight'),
    setSubNavigationSafeAreaHeight: noProviderFunction('setSubNavigationSafeAreaHeight'),
    setOfferBarHeight: noProviderFunction('setOfferBarHeight'),
    headerTheme: null,
    topOfPageTheme: mutableValue(null),
    setHeaderTheme: noProviderFunction('setHeaderThemeProvider'),
    headerVariant: null,
    setHeaderVariant: noProviderFunction('setHeaderVariant'),
});
export const useHeader = () => useContext(HeaderContext);
/**
 * @internal
 */
export const HeaderProvider = ({ children, }) => {
    var _a, _b;
    const headerVisible = useMutableValue(true);
    const headerHeight = useMutableValue(0);
    const baseHeaderHeight = useMutableValue(0);
    const headerPosition = useMutableValue(HeaderPositions.static);
    const topOfPageTheme = useMutableValue(null);
    const [safeAreaHeight, setSafeAreaHeight] = useState(0);
    const [subNavigationSafeAreaHeight, setSubNavigationSafeAreaHeight] = useState(0);
    const [offerBarHeight, setOfferBarHeight] = useState(0);
    const [headerTheme, setHeaderTheme] = useState(null);
    const [headerVariant, setHeaderVariant] = useState(null);
    const showHeader = useCallback(() => {
        headerVisible.set(true);
    }, [headerVisible]);
    const hideHeader = useCallback(() => {
        headerVisible.set(false);
    }, [headerVisible]);
    const currentPath = useRef(null);
    const isSSR = useIsSSR();
    if (!isSSR) {
        if (((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.pathname) !== currentPath.current) {
            currentPath.current = (_b = window === null || window === void 0 ? void 0 : window.location) === null || _b === void 0 ? void 0 : _b.pathname;
            /**
             * Whenever a page is routed without a hard refresh then React.Context is not updated but keeps its value.
             * Gatsby / Remix / Nextjs frameworks all provide this which is fine for other contexts, but in some use-cases it will ensure that a different start state occurs which provided weird rendering errors.
             *
             * Therefore a reset is necessary whenever a path changed.
             *
             * Note: whenever the above settings change then ensure to test the following user flows:
             * - open up a dark theme page (homepage or Polestar 1) and go to a page that starts with a light theme (for example a tranparant Header variant).
             * - check by scrolling a page that has multiple ThemeRegions how the Header theming behaves
             */
            setHeaderTheme(null);
            setSafeAreaHeight(0);
            setOfferBarHeight(0);
            setHeaderVariant(null);
            topOfPageTheme.set(null);
            headerVisible.set(true);
            headerHeight.set(0);
            baseHeaderHeight.set(0);
            headerPosition.set(HeaderPositions.static);
        }
    }
    return (React.createElement(HeaderContext.Provider, { value: {
            showHeader,
            hideHeader,
            headerVisible,
            headerPosition,
            headerHeight,
            baseHeaderHeight,
            safeAreaHeight,
            setSafeAreaHeight,
            subNavigationSafeAreaHeight,
            setSubNavigationSafeAreaHeight,
            offerBarHeight,
            setOfferBarHeight,
            headerTheme,
            topOfPageTheme,
            setHeaderTheme,
            headerVariant,
            setHeaderVariant,
        } }, children));
};
